import React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { updateChallenge, Challenge } from "@/services/challengeService";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { collection, getDocs } from "firebase/firestore";
import { db } from "@/lib/firebase";

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  position?: string;
}

const formSchema = z.object({
  title: z.string().min(3, { message: "Titel muss mindestens 3 Zeichen lang sein." }),
  description: z.string().min(10, { message: "Beschreibung muss mindestens 10 Zeichen lang sein." }),
  startDate: z.date({ required_error: "Startdatum ist erforderlich." }),
  endDate: z.date({ required_error: "Enddatum ist erforderlich." }),
  metricType: z.enum(["leads_closed", "leads_contacted", "new_leads"], { 
    required_error: "Art der Challenge ist erforderlich." 
  }),
  participants: z.array(z.string()).min(1, { message: "Mindestens ein Teilnehmer muss ausgewählt werden." }),
}).refine(data => data.endDate > data.startDate, {
  message: "Das Enddatum muss nach dem Startdatum liegen.",
  path: ["endDate"],
});

type FormValues = z.infer<typeof formSchema>;

interface EditChallengeDialogProps {
  open: boolean;
  onClose: () => void;
  challenge: Challenge;
}

export function EditChallengeDialog({ open, onClose, challenge }: EditChallengeDialogProps) {
  const { currentUser } = useAuth();
  
  // Fetch users for selection
  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const usersRef = collection(db, "users");
      const snapshot = await getDocs(usersRef);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as User)).filter(user => user.active !== false);
    },
  });
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: challenge.title,
      description: challenge.description,
      startDate: new Date(challenge.startDate),
      endDate: new Date(challenge.endDate),
      metricType: challenge.metricType,
      participants: challenge.participants || [],
    },
  });
  
  const updateChallengeMutation = useMutation({
    mutationFn: ({ challengeId, data }: { challengeId: string; data: Partial<Challenge> }) => 
      updateChallenge(challengeId, data),
    onSuccess: () => {
      toast({
        title: "Challenge aktualisiert",
        description: "Die Challenge wurde erfolgreich aktualisiert.",
      });
      onClose();
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Die Challenge konnte nicht aktualisiert werden. Bitte versuchen Sie es später erneut.",
      });
    },
  });
  
  const onSubmit = (values: FormValues) => {
    if (!currentUser) return;
    
    updateChallengeMutation.mutate({
      challengeId: challenge.id,
      data: {
        title: values.title,
        description: values.description,
        startDate: values.startDate,
        endDate: values.endDate,
        metricType: values.metricType,
        participants: values.participants,
      },
    });
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Challenge bearbeiten</DialogTitle>
          <DialogDescription>
            Bearbeite die Details der Challenge.
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Challenge-Titel</FormLabel>
                  <FormControl>
                    <Input placeholder="z.B. 'Top Verkäufer des Monats'" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Beschreibung</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Beschreibe die Challenge und deren Ziele..." 
                      className="min-h-[100px]" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Startdatum</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP", { locale: de })
                            ) : (
                              <span>Datum wählen</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Enddatum</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP", { locale: de })
                            ) : (
                              <span>Datum wählen</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) => {
                            const startDate = form.getValues().startDate;
                            return startDate && date < startDate;
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="metricType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Art der Challenge</FormLabel>
                  <Select 
                    onValueChange={field.onChange} 
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Wähle die Art der Challenge" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="leads_closed">Gewonnene Kunden</SelectItem>
                      <SelectItem value="leads_contacted">Vereinbarte Termine</SelectItem>
                      <SelectItem value="new_leads">Neue Leads</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="participants"
              render={() => (
                <FormItem>
                  <div className="mb-2">
                    <FormLabel>Teilnehmer</FormLabel>
                    <FormDescription>
                      Wähle die Teilnehmer für diese Challenge aus.
                    </FormDescription>
                  </div>
                  <ScrollArea className="h-[200px] rounded-md border p-4">
                    <div className="space-y-4">
                      {users.map((user) => (
                        <FormField
                          key={user.id}
                          control={form.control}
                          name="participants"
                          render={({ field }) => {
                            return (
                              <FormItem
                                key={user.id}
                                className="flex flex-row items-start space-x-3 space-y-0"
                              >
                                <FormControl>
                                  <Checkbox
                                    checked={field.value?.includes(user.id)}
                                    onCheckedChange={(checked) => {
                                      const currentValue = field.value || [];
                                      return checked
                                        ? field.onChange([...currentValue, user.id])
                                        : field.onChange(
                                            currentValue.filter((value) => value !== user.id)
                                          );
                                    }}
                                  />
                                </FormControl>
                                <div className="space-y-1 leading-none">
                                  <FormLabel className="font-normal">
                                    {user.name}
                                    {user.position && (
                                      <span className="text-muted-foreground ml-2">
                                        ({user.position})
                                      </span>
                                    )}
                                  </FormLabel>
                                </div>
                              </FormItem>
                            );
                          }}
                        />
                      ))}
                    </div>
                  </ScrollArea>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter className="pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose}
                disabled={updateChallengeMutation.isPending}
              >
                Abbrechen
              </Button>
              <Button 
                type="submit"
                disabled={updateChallengeMutation.isPending}
              >
                {updateChallengeMutation.isPending ? "Wird gespeichert..." : "Änderungen speichern"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
} 
